import { RSAA, RSAAAction } from 'redux-api-middleware';

import { compareCountriesDSPs } from 'components/notifications/Settings/utils/helpers';

import { EntityTypes } from 'context/entityContext';
import { RootState } from 'redux/rootReducer';
import { Dispatch } from 'redux';
import { TGetRootState } from 'types/shared';

const REQUEST = 'opus/subscriptions/REQUEST';
const GET_SUCCESS = 'opus/subscriptions/GET_SUCCESS';
const POST_SUCCESS = 'opus/subscriptions/POST_SUCCESS';
const DELETE_ALL_SUCCESS = 'opus/subscriptions/DELETE_ALL_SUCCESS';
const DELETE_SUCCESS = 'opus/subscriptions/DELETE_SUCCESS';
const FAILURE = 'opus/subscriptions/FAILURE';
const GET_FAILURE = 'opus/subscriptions/GET_FAILURE';

export interface IEventChannel {
  channelId: ChannelTypes;
  eventTypes?: Array<EventTypes>;
  eventCategories: EventCategories[];
}

export interface ISubscription {
  entityId: string;
  entityType: EntityTypes;
  countryId?: string;
  customerId?: number;
  regionId?: any;
  eventChannels: IEventChannel[];
  id?: number;
}

interface ISubscriptionState {
  loading: boolean;
  success: boolean;
  error: undefined | Error;
  errorInfo: undefined | string;
  data: undefined | IApplicationSubscriptions;
}

interface IApplicationSubscriptions {
  applicationId: string;
  subscriptions: ISubscription[];
}

export type ChannelTypes =
  | 'email_weekly_digest'
  | 'email_daily_digest'
  | 'standalone_email'
  | 'on_site'
  | 'push';

export type EventCategories =
  | 'playlisting'
  | 'thresholds'
  | 'charts'
  | 'priority';

export type EventTypes =
  | 'playlist_add'
  | 'playlist_removal'
  | 'playlist_move_up'
  | 'playlist_new_peak'
  | 'priority_playlist_add'
  | 'priority_playlist_removal'
  | 'chart_add'
  | 'chart_move_up'
  | 'chart_move_down'
  | 'chart_new_peak'
  | 'chart_removal'
  | 'streaming_performance_threshold'
  | 'streaming_wow_completed_growth_threshold'
  | 'streaming_wow_in_progress_growth_threshold'
  | 'streaming_daily_growth_threshold'
  | 'downloads_album_performance_threshold'
  | 'downloads_album_wow_completed_growth_threshold'
  | 'downloads_album_wow_in_progress_growth_threshold'
  | 'downloads_album_daily_growth_threshold'
  | 'downloads_track_performance_threshold'
  | 'downloads_track_wow_completed_growth_threshold'
  | 'downloads_track_wow_in_progress_growth_threshold'
  | 'downloads_track_daily_growth_threshold'
  | 'physical_performance_threshold'
  | 'physical_wow_completed_growth_threshold'
  | 'physical_wow_in_progress_growth_threshold'
  | 'physical_daily_growth_threshold'
  | 'equivalents_performance_threshold'
  | 'equivalents_wow_completed_growth_threshold'
  | 'equivalents_wow_in_progress_growth_threshold'
  | 'equivalents_daily_growth_threshold';

type SubscriptionAction =
  | {
      type: typeof POST_SUCCESS;
      payload: ISubscription;
      entityType: EntityTypes;
      error?: any;
    }
  | {
      type: typeof DELETE_SUCCESS;
      payload: { id: number };
      error?: any;
    }
  | {
      type: typeof DELETE_ALL_SUCCESS;
      payload: { entityType: EntityTypes; entityId: string };
      entityType: EntityTypes;
      error?: any;
    }
  | {
      type: typeof REQUEST;
    }
  | {
      type: typeof GET_SUCCESS;
      payload: IApplicationSubscriptions[];
      entityType: EntityTypes;
      error?: any;
    }
  | {
      type: typeof FAILURE;
      payload: {
        error: any;
        errorInfo: any;
        status: number;
        statusText: string;
      };
    }
  | {
      type: typeof GET_FAILURE;
      payload: {
        error: any;
        errorInfo: any;
        status: number;
        statusText: string;
      };
    };

const initialState: ISubscriptionState = {
  loading: false,
  success: false,
  error: undefined,
  errorInfo: undefined,
  data: undefined,
};

/**
 * # Entity Notification Subscriptions
 * This reducer manages all the entity subscriptions for the current user.
 *
 * By dispatching setSubscription the given ISubscription will be replaced or added
 * in the global subscriptions list by performing an API call, based on whether a subscription for the given
 * entityId already exists or not. The redux store will afterwards be updated, reflecting the
 * changes.
 *
 * By dispatching removeSubscription the ISubscription with the given entityId
 * will be removed from the global subscriptions list by performing an API call. The redux store will afterwards
 * be updated, reflecting the changes.
 *
 * shouldLoadSubscriptions will check if the subscriptions are loading are have been loaded.
 *
 * loadSubscriptions will load all the subscriptions for the current user.
 */
export default function reducer(
  state: ISubscriptionState = initialState,
  action: SubscriptionAction
): ISubscriptionState {
  switch (action.type) {
    case REQUEST:
      return {
        ...state,
        loading: true,
      };
    case GET_SUCCESS: {
      const data = action.payload
        ? action.payload.find((item: IApplicationSubscriptions) => {
            if (item.applicationId === 'opus') {
              return item;
            }
            return undefined;
          })
        : {
            applicationId: 'opus',
            subscriptions: [],
          };

      return {
        ...state,
        loading: false,
        success: true,
        error: undefined,
        errorInfo: undefined,
        data: data,
      };
    }
    case POST_SUCCESS:
      return {
        ...state,
        loading: false,
        success: true,
        error: undefined,
        errorInfo: undefined,
        data: {
          applicationId: state.data?.applicationId ?? 'opus',
          subscriptions: updateSubscription(state, action.payload),
        },
      };
    case DELETE_SUCCESS:
      return {
        ...state,
        loading: false,
        success: true,
        data: {
          applicationId: state.data?.applicationId ?? 'opus',
          subscriptions: deleteSubscription(state, action.payload),
        },
      };
    case DELETE_ALL_SUCCESS:
      return {
        ...state,
        loading: false,
        success: true,
        data: {
          applicationId: state.data?.applicationId ?? 'opus',
          subscriptions: deleteSubscriptionsForArtist(state, action.payload),
        },
      };
    case GET_FAILURE:
      if (action.payload.status === 404) {
        return {
          ...state,
          loading: false,
          success: true,
          data: {
            applicationId: 'opus',
            subscriptions: [],
          },
        };
      }
      return {
        ...state,
        loading: false,
        success: false,
        error: new Error(action.payload.error),
        errorInfo: action.payload.errorInfo,
      };
    case FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: new Error(action.payload.error),
        errorInfo: action.payload.errorInfo,
      };

    default:
      return state;
  }
}

function deleteSubscription(
  state: ISubscriptionState,
  payload: { id: number }
): ISubscription[] {
  if (!state || !state.data || !state.data.subscriptions) {
    return [];
  }

  const { id } = payload;
  const subscriptions = state.data.subscriptions;

  function filterBySubId(subscription: ISubscription): boolean {
    return subscription.id !== id;
  }

  return subscriptions.filter(filterBySubId);
}

function deleteSubscriptionsForArtist(
  state: ISubscriptionState,
  payload: { entityType: EntityTypes; entityId: string }
): ISubscription[] {
  if (!state || !state.data || !state.data.subscriptions) {
    return [];
  }

  const subscriptions = state.data.subscriptions;
  const newSubscriptions = subscriptions.filter(filterByEntityId);

  function filterByEntityId(subscription: ISubscription): boolean {
    return subscription.entityId !== payload.entityId;
  }

  return newSubscriptions;
}

function updateSubscription(
  state: ISubscriptionState,
  subscription: ISubscription
): ISubscription[] {
  if (!state || !state.data || !state.data.subscriptions) {
    return [subscription];
  }

  const subscriptions = state.data.subscriptions;

  const existingSub = subscriptions.find((sub: ISubscription): boolean => {
    const isSameEntityId = sub.entityId === subscription.entityId;
    const isSameCountriesDSPs = compareCountriesDSPs(sub, subscription);

    return isSameCountriesDSPs && isSameEntityId;
  });

  if (!existingSub) {
    subscriptions.push(subscription);
  } else {
    const idx = subscriptions.indexOf(existingSub);
    subscriptions[idx] = subscription;
  }

  return subscriptions;
}

export function shouldLoadSubscriptions(): (
  dispatch: Dispatch,
  getState: TGetRootState
) => boolean {
  return (_: Dispatch, getState: TGetRootState): boolean => {
    const { subscriptions } = getState();
    if (subscriptions.loading) {
      return false;
    }
    return !subscriptions.loading && !subscriptions.data;
  };
}

export function loadSubscriptions() {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const { subscriptions } = getState();

    const response = await dispatch({
      [RSAA]: {
        bailout: subscriptions.loading || subscriptions.success,
        types: [
          REQUEST,
          {
            type: GET_SUCCESS,
            payload: async function payload(
              _action: RSAAAction,
              _state: RootState,
              res: any
            ): Promise<any> {
              // TODO: clean this up!
              return res.json();
            },
          },
          GET_FAILURE,
        ],
        endpoint: `/notifications/subscriptions/me`,
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
        credentials: 'same-origin',
      },
    });

    return response;
  };
}

export function setSubscription(
  subscription: ISubscription,
  optionalCallback?: any
) {
  return async function (dispatch: Dispatch): Promise<any> {
    const response = await dispatch({
      [RSAA]: {
        types: [
          { type: REQUEST },
          {
            type: POST_SUCCESS,
            payload: subscription,
          },
          { type: FAILURE },
        ],
        endpoint: '/notifications/subscriptions/applications/opus',
        method: 'POST',
        body: JSON.stringify(subscription),
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    }).then(() => {
      if (optionalCallback) {
        return dispatch(optionalCallback());
      }
    });

    return response;
  };
}

export function removeAllSubscriptions(
  entityId: string,
  entityType: EntityTypes = 'artist'
) {
  return async function (dispatch: Dispatch): Promise<any> {
    const response = await dispatch({
      [RSAA]: {
        types: [
          { type: REQUEST },
          { type: DELETE_ALL_SUCCESS, payload: { entityType, entityId } },
          { type: FAILURE },
        ],
        endpoint: `/notifications/subscriptions/applications/opus/entityType/${entityType}/entityId/${entityId}`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });

    return response;
  };
}

export function removeSubscription(id: number) {
  return async function (dispatch: Dispatch): Promise<any> {
    const response = await dispatch({
      [RSAA]: {
        types: [
          { type: REQUEST },
          { type: DELETE_SUCCESS, payload: { id } },
          { type: FAILURE },
        ],
        endpoint: `/notifications/subscriptions/${id}`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });

    return response;
  };
}

export function selectSubscriptions(
  state: RootState,
  entityType: EntityTypes
): [null, boolean] | [ISubscription[], boolean] {
  const { data, loading } = state.subscriptions;

  if (!data) return [null, loading];

  const entitySubscriptions = data.subscriptions.filter(
    (subscription: ISubscription) => subscription.entityType === entityType
  );
  return [entitySubscriptions, loading];
}

export function selectSubscriptionsForEntity(entityId: string) {
  return function (state: RootState): null | ISubscription[] {
    const { data, loading } = state.subscriptions;

    if (loading || !data || !data.subscriptions) return null;

    const isSameId = (item: ISubscription): boolean =>
      item.entityId === entityId;
    const subscriptions = data.subscriptions.filter(isSameId);

    if (subscriptions && subscriptions.length > 0) return subscriptions;
    else return null;
  };
}
